import { CONVOY, EVENT_ROTATION, EVENT_SLOT_MS, GIANT, INVASION, } from '../data/world-events.js';
/** The slot a wall-clock time falls in. */
export function eventSlotOf(now) {
    return Math.floor(now / EVENT_SLOT_MS);
}
/** What a slot holds, from the rotation: the same answer everywhere, nothing stored. */
export function scheduledEvent(slot) {
    const length = EVENT_ROTATION.length;
    const turn = EVENT_ROTATION[((slot % length) + length) % length] ?? {
        kind: 'invasion',
        biome: 'meadow',
    };
    return { slot, kind: turn.kind, biome: turn.biome, startsAt: slot * EVENT_SLOT_MS };
}
/** The next `count` events that have not started yet, soonest first. */
export function upcomingEvents(now, count) {
    const first = eventSlotOf(now) + 1;
    return Array.from({ length: Math.max(0, count) }, (_, k) => scheduledEvent(first + k));
}
/** Points of each deed. A kill is worth a raider's worth of blows at the meadow's level. */
export const CONTRIBUTION_POINTS = Object.freeze({
    dealt: 1,
    taken: 0.5,
    kill: 25,
    escortSecond: 2,
});
export function emptyDeeds() {
    return { dealt: 0, taken: 0, kills: 0, escortSeconds: 0 };
}
/** A defender's score, in whole points. */
export function contributionScore(deeds) {
    return Math.floor(deeds.dealt * CONTRIBUTION_POINTS.dealt +
        deeds.taken * CONTRIBUTION_POINTS.taken +
        deeds.kills * CONTRIBUTION_POINTS.kill +
        deeds.escortSeconds * CONTRIBUTION_POINTS.escortSecond);
}
/**
 * The share of the mean a defender must reach to be rewarded. Relative to
 * the mean, not to the total: at fifty defenders the mean share is 2 %, and
 * a fixed 5 % would leave nearly everyone out.
 */
export const EVENT_SHARE_THRESHOLD = 0.25;
/** How many stand on the podium. */
export const PODIUM_SIZE = 3;
/** The score a defender must reach: a quarter of the mean share, at least one point. */
export function eventThreshold(scores) {
    const scored = scores.filter((score) => score > 0);
    if (scored.length === 0)
        return 1;
    const mean = scored.reduce((sum, score) => sum + score, 0) / scored.length;
    return Math.max(1, Math.ceil(mean * EVENT_SHARE_THRESHOLD));
}
/**
 * The ranking of an event: every defender who scored, best first. Entries
 * come in the order they first scored, which breaks ties. The podium is the
 * first three over the threshold.
 */
export function rankDefenders(entries) {
    const scored = entries.filter((entry) => entry.score > 0);
    const total = scored.reduce((sum, entry) => sum + entry.score, 0);
    const threshold = eventThreshold(scored.map((entry) => entry.score));
    return scored
        .map((entry, order) => ({ ...entry, order }))
        .sort((a, b) => b.score - a.score || a.order - b.order)
        .map((entry, k) => {
        const eligible = entry.score >= threshold;
        return {
            id: entry.id,
            score: entry.score,
            rank: k + 1,
            share: total === 0 ? 0 : entry.score / total,
            eligible,
            podium: eligible && k < PODIUM_SIZE,
        };
    });
}
/** Raiders in a wave, for this many defenders about. */
export function waveSize(defenders) {
    const { base, perDefender, cap } = INVASION.raiders;
    return Math.min(cap, base + Math.ceil(perDefender * Math.max(0, defenders - 1)));
}
/** How much tougher each assailant is against this many defenders: health and damage multipliers. */
export function foeMight(defenders) {
    const extra = Math.max(0, defenders - 1);
    const { health, damage } = INVASION.might;
    return {
        health: Math.min(health.max, 1 + health.perDefender * extra),
        damage: Math.min(damage.max, 1 + damage.perDefender * extra),
    };
}
/**
 * Who marches in a wave of `count`, in the order they are sent: the
 * region's creatures (`creatures`, its dens) taken in turn for their share,
 * goblin raiders for the rest, mixed so every gate gets both.
 */
export function waveRoster(count, raider, creatures) {
    const beasts = creatures.length === 0 ? 0 : Math.round(count * INVASION.creatureShare);
    const roster = [];
    let sent = 0;
    for (let k = 0; k < count; k += 1) {
        // Beasts spread evenly among the goblins: every other one at half and half.
        const beast = sent < beasts && Math.floor(((k + 1) * beasts) / count) > sent;
        if (beast) {
            roster.push(creatures[sent % creatures.length] ?? raider);
            sent += 1;
        }
        else
            roster.push(raider);
    }
    return roster;
}
/** The warlord's health against this many defenders: each one past the first adds a share. */
export function warlordHealth(base, defenders) {
    const { perDefender, maxScale } = INVASION.warlord;
    const scale = Math.min(maxScale, 1 + perDefender * Math.max(0, defenders - 1));
    return Math.round(base * scale);
}
/**
 * The giant's health against this many fighters about it: each one past the
 * first adds a share. It only ever grows: a crowd that gathers late finds it
 * as tough as one there from the start.
 */
export function giantHealth(base, fighters) {
    const { perFighter, maxScale } = GIANT.health;
    const scale = Math.min(maxScale, 1 + perFighter * Math.max(0, fighters - 1));
    return Math.round(base * scale);
}
/** Raiders in a wave on the cart, for this many escorts. */
export function convoyWaveSize(escorts) {
    const { base, perEscort, cap } = CONVOY.raiders;
    return Math.min(cap, base + Math.ceil(perEscort * Math.max(0, escorts - 1)));
}
/**
 * Whether the cart rolls on: someone beside it, no raider near it. A cart
 * that halts for nobody would be taken by anyone who runs ahead alone.
 */
export function cartRolls(escorts, raidersNear) {
    return escorts > 0 && raidersNear === 0;
}
//# sourceMappingURL=world-events.js.map